import React from "react";
import { Button, Modal } from "react-bootstrap";
import { gql, useMutation } from "@apollo/client";
import moment from "moment";

import { PlaylistForm } from "../../forms";

function PlaylistModal({ as: Component, children, playlist, ...props }) {
  // Mutations
  const [createPlaylistMutation, {}] = useMutation(CREATE_PLAYLIST_MUTATION);
  const [updatePlaylistMutation, {}] = useMutation(EDIT_PLAYLIST_MUTATION);
  const [deletePlaylistMutation, {}] = useMutation(DELETE_PLAYLIST_MUTATION);

  // Helper to make lists mutable (needed after summer 2025 updates)
  const makeMutableList = (list) =>
    Array.isArray(list) ? list.map(item => ({ ...(item || {}) })) : [];

  // Form State
  const [formState, setFormState] = React.useState(
    playlist && typeof playlist === "object"
      ? { ...playlist, show: playlist?.show?.id ?? playlist.show }
      : {
          show: 0,
          date: moment().format("YYYY-MM-DD"),
        }
  );

  const [songsFormState, setSongsFormState] = React.useState(
    playlist && Array.isArray(playlist.songs) && playlist.songs.length
      ? makeMutableList(playlist.songs)
      : [{ _id: 1 }, { _id: 2 }]
  );


  // Modal Display
  const [show, setShow] = React.useState(false);

  // Handlers
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const handleSave = () => {
    if (!formState.show) {
      alert("The show field is empty!");
      return;
    }

    // Clean up our songs form state
    let songs = [];
    for (let i = 0; i < songsFormState.length; i++) {
      const keys = Object.keys(songsFormState[i]);
      for (let j = 0; j < keys.length; j++) {
        if (keys[j] != "_id" && songsFormState[i][keys[j]]) {
          // Make sure the songs have a field that isn't empty
          let song = { ...songsFormState[i] };
          delete song.chosen;
          delete song.selected;
          delete song._id;
          // Handle deleting typename if it exists
          if (song.id) delete song.__typename;
          songs.push(song);
          break;
        }
      }
    }

    if (playlist || formState.id) {
      // Create a new form state and get rid of typename variable
      let newFormState = { ...formState };
      delete newFormState.__typename;
      updatePlaylistMutation({ variables: { ...newFormState, songs } }).then(
        () => window.location.reload()
      );
    } else
      createPlaylistMutation({
        variables: {
          ...formState,
          songs,
        },
      }).then(() => window.location.reload());

    setShow(false);
  };

  return (
    <>
      <Component {...props} onClick={handleShow}>
        {children}
      </Component>

      <Modal
        size="lg"
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>New Playlist</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <PlaylistForm
            playlist={playlist}
            state={formState}
            setState={setFormState}
            songs={songsFormState}
            setSongs={setSongsFormState}
          />
        </Modal.Body>
        <Modal.Footer>
          {playlist ? (
            <Button
              variant="danger"
              className="me-auto"
              onClick={() => {
                deletePlaylistMutation({
                  variables: { id: formState.id },
                }).then(() => window.location.reload());
              }}
            >
              Delete
            </Button>
          ) : null}
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default PlaylistModal;

const CREATE_PLAYLIST_MUTATION = gql`
  mutation CreatePlaylist(
    $show: ID!
    $date: Date!
    $title: String
    $description: String
    $songs: [SongInputType]
  ) {
    createPlaylist(
      show: $show
      date: $date
      title: $title
      description: $description
      songs: $songs
    ) {
      playlist {
        id
      }
    }
  }
`;

const EDIT_PLAYLIST_MUTATION = gql`
  mutation EditPlaylist(
    $id: ID!
    $show: ID!
    $date: Date!
    $title: String
    $description: String
    $songs: [SongInputType]
  ) {
    updatePlaylist(
      id: $id
      show: $show
      date: $date
      title: $title
      description: $description
      songs: $songs
    ) {
      playlist {
        id
      }
    }
  }
`;

const DELETE_PLAYLIST_MUTATION = gql`
  mutation DeletePlaylist($id: ID!) {
    deletePlaylist(id: $id) {
      id
    }
  }
`;
